import React, { useEffect, useRef, useState } from 'react';
import { Award, Users, Truck, Sparkles } from 'lucide-react';
import Footer from './Footer';

// Shared scroll reveal hook
const useReveal = (threshold = 0.12) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [threshold]);

  return [ref, isVisible];
};

const valuesData = [
  {
    icon: Award,
    title: 'Design',
    description: 'Our Graphic Designer & Visualizer Team is creative & professional to match corporate need in quality and time bound services.',
  },
  {
    icon: Users,
    title: 'Printing',
    description: 'Our Backbone is our latest technology machinery (Heidelberg, Lithography, Pollar, Konica Minolta). Experienced Printing Staff work day & night to match your Pan India requirement on time.',
  },
  {
    icon: Truck,
    title: 'Packing & Dispatch',
    description: 'Printed Goods are packed location wise in Poly Seal Bag, Good for all season. With complete responsibility our Dispatch team hand over goods to your Courier company, & collect C. Note.',
  },
  {
    icon: Sparkles,
    title: 'MIS Maintaining',
    description: 'Upload C. Note detail in Excel File, mail the same for your record & submit hard copy along with invoice. Feedback & Status Reply Service - We reply & provide Despatch detail to all branches across India.',
  },
];

const whyChooseData = [
  {
    title: 'Our Advantage',
    description: 'Feedback & Status Reply Service - We reply & provide Despatch detail to all branches across India (Scan Copy of C. Note) & save time of Admin Dept. & keep them relaxed too.',
  },
  {
    title: 'Latest Technology',
    description: 'Our Backbone is our latest technology machinery (Heidelberg, Lithography, Pollar, Konica Minolta). Experienced Printing Staff work day & night to match your Pan India requirement on time.',
  },
  {
    title: 'Complete Service',
    description: 'From Design to Printing, Packing (location wise in Poly Seal Bag), Dispatch with complete responsibility, and MIS Maintaining with detailed documentation.',
  },
];

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [heroRef, heroVisible] = useReveal();
  const [whoRef, whoVisible] = useReveal();
  const [valuesHeaderRef, valuesHeaderVisible] = useReveal();
  const [missionRef, missionVisible] = useReveal();
  const [whyHeaderRef, whyHeaderVisible] = useReveal();

  return (
    <div className="bg-white text-black min-h-screen">
      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div ref={heroRef} className={`reveal ${heroVisible ? 'visible' : ''}`}>
            <h1 className="text-6xl md:text-8xl font-bold text-center mb-6">About Mahadev Printers</h1>
            <p className="text-xl md:text-2xl text-gray-600 text-center max-w-4xl mx-auto">
              The Ultimate Printing Hub - Pan India Service. We print your imaginations with cutting-edge technology and expert craftsmanship.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div ref={whoRef} className={`reveal ${whoVisible ? 'visible' : ''}`}>
              <h2 className="text-4xl md:text-5xl font-bold mb-8">Who We Are</h2>
              <h3 className="text-3xl font-bold mb-6">WE ARE CORPORATE PRINTER</h3>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  One of the most evident ramifications of Globalization has been the tremendous growth in the world of commerce. This globalization understands the nuances of printing and can provide complete printing solutions to their customers through highest levels of service standards.
                </p>
                <p>
                  We at "Mahadev Printers" believe in rendering world class printing services to our esteemed customers which enables the organization to prolong the long term association with the customers in this ever changing competitive world of printing. We understand that a satisfied customer is the bench mark of success in the intangible service industry. Therefore "Prints your Imaginations" is just not a slogan, it is the motivation around which the responsibilities are executed at "Mahadev Printers".
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Stand For */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div ref={valuesHeaderRef} className={`reveal ${valuesHeaderVisible ? 'visible' : ''}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">What We Stand For</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {valuesData.map((item, i) => (
              <ValueCard key={item.title} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div ref={missionRef} className={`reveal ${missionVisible ? 'visible' : ''}`}>
              <h2 className="text-4xl md:text-5xl font-bold mb-8">Our Mission</h2>
              <p className="text-2xl text-gray-700 leading-relaxed mb-6">
                We deliver creative Print & Marketing Solutions that make our clients successful.
              </p>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                We keep our promises, provide creative solutions & achieve our client's goals.
              </p>
              <h3 className="text-3xl font-bold text-black mt-8">YOU THINK IT, WE INK IT!</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6">
          <div ref={whyHeaderRef} className={`reveal ${whyHeaderVisible ? 'visible' : ''}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">Why Choose Mahadev Printers?</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-8">
            {whyChooseData.map((item, i) => (
              <WhyChooseItem key={item.title} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ValueCard = ({ item, index }) => {
  const [ref, isVisible] = useReveal(0.08);
  const Icon = item.icon;

  return (
    <div ref={ref} className={`reveal reveal-delay-${index + 1} ${isVisible ? 'visible' : ''}`}>
      <div className="card-animate rounded-xl bg-white p-8 shadow-md border border-gray-100/60">
        <Icon className="w-12 h-12 mb-4 text-black icon-hover" />
        <h3 className="text-xl font-bold mb-3">{item.title}</h3>
        <p className="text-gray-600 leading-relaxed">{item.description}</p>
      </div>
    </div>
  );
};

const WhyChooseItem = ({ item, index }) => {
  const [ref, isVisible] = useReveal(0.08);

  return (
    <div ref={ref} className={`reveal reveal-delay-${index + 1} ${isVisible ? 'visible' : ''}`}>
      <div className="border-l-4 border-white pl-6">
        <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
        <p className="text-gray-300 text-lg">{item.description}</p>
      </div>
    </div>
  );
};

export default AboutPage;
